import React from 'react';
import { ActionIcon, Box, Group, Stack, Text, Tooltip } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import ValidationIcon from '../complex/ValidationIcon';
import { ArrayTranslations, JsonSchema, JsonSchema7 } from '@jsonforms/core';
import { usePresetContext } from '../../contexts/PresetContext';
import { getDepth } from '../../utils/navigation';

export interface ArrayLayoutToolbarProps {
  label: string;
  description: string;
  errors: string;

  path: string;
  addItem(path: string, data: any): () => void;
  createDefault(): any;

  enabled: boolean;
  disableAdd?: boolean;
  minItems?: number;
  removeAllItems?: (path: string, toDelete: number[]) => () => void;

  translations: ArrayTranslations;

  itemsNumber: number;

  schema: JsonSchema;
}

export const ArrayLayoutToolbar = React.memo(function ArrayLayoutToolbar({
  label,
  description,
  errors,
  addItem,
  path,
  enabled,
  createDefault,
  translations,
  disableAdd,
  itemsNumber,
  minItems,
  removeAllItems,
  schema,
}: ArrayLayoutToolbarProps) {
  const { removeFromFormData, addToFormData } = usePresetContext();

  const onClick = () => {
    if (typeof minItems === 'undefined') {
      addToFormData(`${path}.${itemsNumber}`, createDefault());

      return;
    }

    const missingItems = minItems - itemsNumber;

    if (missingItems <= 0) {
      addToFormData(`${path}.${itemsNumber}`, createDefault());

      return;
    }

    for (let i = 0; i < missingItems; i++) {
      addItem(path, createDefault())();
    }
  };

  const handleRemoveAllItems = () => {
    removeFromFormData(path.split('.').slice(0, -1).join('.'));
  };

  return (
    <Box>
      <Stack gap='xs'>
        <Group align='center'>
          <Group gap='md' align='center'>
            <Text fz={`h${getDepth(path) + 2}`}>{label}</Text>
            {errors.length !== 0 && <ValidationIcon id='tooltip-validation' errorMessages={errors} />}
          </Group>
          {enabled && !disableAdd && (
            <Tooltip label={translations.addTooltip} position='bottom'>
              <ActionIcon onClick={onClick}>
                <IconPlus />
              </ActionIcon>
            </Tooltip>
          )}
          {!removeAllItems || itemsNumber === 0 ? null : (
            <Tooltip label='Remove all items' position='bottom'>
              <ActionIcon onClick={handleRemoveAllItems} bg='red.4'>
                <IconTrash />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
        {description && (
          <Text c='gray.6' size='sm'>
            {description}
          </Text>
        )}
      </Stack>
    </Box>
  );
});
